/**
 * Public shared constants API for @stockforge/shared/constants.
 * Constants exported here are intentionally shared by web, mobile, and API tests.
 */

// ============================================
// Application Constants
// ============================================

export const APP_NAME = 'StockForge';

export const ONBOARDING_STEPS = [
  { id: 0, label: 'Welcome' },
  { id: 1, label: 'Organization' },
  { id: 2, label: 'Warehouse' },
  { id: 3, label: 'Materials' },
  { id: 4, label: 'Recipes' },
  { id: 5, label: 'Team' },
  { id: 6, label: 'Preferences' },
] as const;

export const ONBOARDING_INDUSTRIES = [
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'cosmetics', label: 'Cosmetics & Personal Care' },
  { value: 'textiles', label: 'Textiles & Apparel' },
  { value: 'chemicals', label: 'Chemicals' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'woodworking', label: 'Woodworking & Furniture' },
  { value: 'metalworking', label: 'Metalworking' },
  { value: 'other', label: 'Other' },
] as const;

export const ONBOARDING_UNIT_SYSTEMS = [
  { value: 'metric', label: 'Metric (kg, L, m)' },
  { value: 'imperial', label: 'Imperial (lb, gal, yd)' },
] as const;

export const ONBOARDING_DATE_FORMATS = [
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-02-28)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (02/28/2026)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (28/02/2026)' },
] as const;

export const ONBOARDING_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'GMT / UTC' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
] as const;

export const ONBOARDING_MATERIAL_UNITS: Record<string, string[]> = {
  LIQUID: ['ml', 'L', 'gal', 'fl_oz'],
  FABRIC: ['yd', 'm', 'ft', 'in'],
  DISCRETE: ['pcs', 'units', 'boxes', 'rolls'],
};

// ============================================
// API Configuration
// ============================================

export const API_CONFIG = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  TIMEOUT_MS: 30000,
  RETRY_COUNT: 3,
  RETRY_DELAY_MS: 1000,
} as const;

// ============================================
// API Endpoints
// ============================================

export const ENDPOINTS = {
  auth: {
    base: '/api/v1/auth',
    login: '/api/v1/auth/login',
    logout: '/api/v1/auth/logout',
    refresh: '/api/v1/auth/refresh',
    me: '/api/v1/auth/me',
    register: '/api/v1/auth/register',
    verifyEmail: '/api/v1/auth/verify-email',
    resendVerification: '/api/v1/auth/resend-verification',
    forgotPassword: '/api/v1/auth/forgot-password',
    resetPassword: '/api/v1/auth/reset-password',
    otpRequest: '/api/v1/auth/otp/request',
    otpVerify: '/api/v1/auth/otp/verify',
    magicLinkRequest: '/api/v1/auth/magic-link/request',
    magicLinkVerify: '/api/v1/auth/magic-link/verify',
    passkeyAuthOptions: '/api/v1/auth/passkeys/auth/options',
    passkeyAuthVerify: '/api/v1/auth/passkeys/auth/verify',
    passkeyRegisterOptions: '/api/v1/auth/passkeys/register/options',
    passkeyRegisterVerify: '/api/v1/auth/passkeys/register/verify',
    passkeys: '/api/v1/auth/passkeys',
    passkeyDelete: (id: string) => `/api/v1/auth/passkeys/${id}`,
  },
  onboarding: {
    base: '/api/v1/onboarding',
    status: '/api/v1/onboarding/status',
    step: '/api/v1/onboarding/step',
    organization: '/api/v1/onboarding/organization',
    complete: '/api/v1/onboarding/complete',
    skip: '/api/v1/onboarding/skip',
    reset: '/api/v1/onboarding/reset',
    invite: '/api/v1/onboarding/invite',
  },
  organizations: {
    base: '/api/v1/organizations',
    create: '/api/v1/organizations',
    list: '/api/v1/organizations',
    get: (id: string) => `/api/v1/organizations/${id}`,
    update: (id: string) => `/api/v1/organizations/${id}`,
    members: (id: string) => `/api/v1/organizations/${id}/members`,
    emailConfig: '/api/v1/organizations/email-config',
    testEmailConfig: '/api/v1/organizations/email-config/test',
    sizeVariants: '/api/v1/organizations/settings/size-variants',
  },
  items: {
    list: '/api/v1/items',
    create: '/api/v1/items',
    get: (id: string) => `/api/v1/items/${id}`,
    update: (id: string) => `/api/v1/items/${id}`,
    delete: (id: string) => `/api/v1/items/${id}`,
    bulkDelete: '/api/v1/items/bulk-delete',
    bulkLowStockEmailAlerts: '/api/v1/items/bulk-low-stock-email-alerts',
    bulkThresholds: '/api/v1/items/bulk-thresholds',
    bulkMetadata: '/api/v1/items/bulk-metadata',
    adjustStock: (id: string) => `/api/v1/items/${id}/adjust`,
    lowStock: '/api/v1/items/low-stock',
    bySku: (sku: string) => `/api/v1/items/sku/${sku}`,
    byBarcode: (barcode: string) => `/api/v1/items/barcode/${barcode}`,
  },
  /** @deprecated Use items. */
  materials: {
    list: '/api/v1/materials',
    create: '/api/v1/materials',
    get: (id: string) => `/api/v1/materials/${id}`,
    update: (id: string) => `/api/v1/materials/${id}`,
    delete: (id: string) => `/api/v1/materials/${id}`,
    bulkDelete: '/api/v1/materials/bulk-delete',
    bulkLowStockEmailAlerts: '/api/v1/materials/bulk-low-stock-email-alerts',
    bulkThresholds: '/api/v1/materials/bulk-thresholds',
    bulkMetadata: '/api/v1/materials/bulk-metadata',
    adjustStock: (id: string) => `/api/v1/materials/${id}/adjust`,
    lowStock: '/api/v1/materials/low-stock',
    bySku: (sku: string) => `/api/v1/materials/sku/${sku}`,
    byBarcode: (barcode: string) => `/api/v1/materials/barcode/${barcode}`,
  },
  recipes: {
    list: '/api/v1/recipes',
    create: '/api/v1/recipes',
    get: (id: string) => `/api/v1/recipes/${id}`,
    update: (id: string) => `/api/v1/recipes/${id}`,
    delete: (id: string) => `/api/v1/recipes/${id}`,
    bySku: (sku: string) => `/api/v1/recipes/sku/${sku}`,
    versions: (id: string) => `/api/v1/recipes/${id}/versions`,
    bomTree: (id: string) => `/api/v1/recipes/${id}/bom-tree`,
    bomFlat: (id: string) => `/api/v1/recipes/${id}/bom-flat`,
    feasibility: (id: string) => `/api/v1/recipes/${id}/feasibility`,
    subAssemblies: (id: string) => `/api/v1/recipes/${id}/sub-assemblies`,
    sizeFamilies: '/api/v1/recipes/size-families',
    optimization: {
      wastage: (id: string) => `/api/v1/recipes/${id}/optimization/wastage`,
      batchSize: (id: string) => `/api/v1/recipes/${id}/optimization/batch-size`,
      costs: (id: string) => `/api/v1/recipes/${id}/optimization/costs`,
      score: (id: string) => `/api/v1/recipes/${id}/optimization/score`,
      summary: (id: string) => `/api/v1/recipes/${id}/optimization/summary`,
      applyWastage: (id: string) => `/api/v1/recipes/${id}/optimization/apply-wastage`,
    },
  },
  kits: {
    list: '/api/v1/kits',
    create: '/api/v1/kits',
    get: (id: string) => `/api/v1/kits/${id}`,
    update: (id: string) => `/api/v1/kits/${id}`,
    delete: (id: string) => `/api/v1/kits/${id}`,
    assemble: (id: string) => `/api/v1/kits/${id}/assemble`,
    feasibility: (id: string) => `/api/v1/kits/${id}/feasibility`,
    import: '/api/v1/kits/import',
  },
  quotes: {
    list: '/api/v1/quotes',
    create: '/api/v1/quotes',
    get: (id: string) => `/api/v1/quotes/${id}`,
    update: (id: string) => `/api/v1/quotes/${id}`,
    submitForApproval: (id: string) => `/api/v1/quotes/${id}/submit-for-approval`,
    refreshApproval: (id: string) => `/api/v1/quotes/${id}/refresh-approval`,
    pdf: (id: string) => `/api/v1/quotes/${id}/pdf`,
    send: (id: string) => `/api/v1/quotes/${id}/send`,
    accept: (id: string) => `/api/v1/quotes/${id}/accept`,
    decline: (id: string) => `/api/v1/quotes/${id}/decline`,
  },
  production: {
    batches: '/api/v1/production',
    createBatch: '/api/v1/production',
    getBatch: (id: string) => `/api/v1/production/${id}`,
    deleteBatch: (id: string) => `/api/v1/production/${id}`,
    todaySummary: '/api/v1/production/summary',
    history: '/api/v1/production',
    buildPlans: '/api/v1/production/build-plans',
    buildPlanBuild: (id: string) => `/api/v1/production/build-plans/${id}/build`,
    buildPlanCancel: (id: string) => `/api/v1/production/build-plans/${id}/cancel`,
  },
  purchaseOrders: {
    base: '/api/v1/purchase-orders',
    list: '/api/v1/purchase-orders',
    create: '/api/v1/purchase-orders',
    get: (id: string) => `/api/v1/purchase-orders/${id}`,
    update: (id: string) => `/api/v1/purchase-orders/${id}`,
    delete: (id: string) => `/api/v1/purchase-orders/${id}`,
    send: (id: string) => `/api/v1/purchase-orders/${id}/send`,
    receive: (id: string) => `/api/v1/purchase-orders/${id}/receive`,
    cancel: (id: string) => `/api/v1/purchase-orders/${id}/cancel`,
  },
  vendors: {
    list: '/api/v1/vendors',
    create: '/api/v1/vendors',
    get: (id: string) => `/api/v1/vendors/${id}`,
    update: (id: string) => `/api/v1/vendors/${id}`,
    delete: (id: string) => `/api/v1/vendors/${id}`,
  },
  dashboard: {
    stats: '/api/v1/materials/stats',
    lowStock: '/api/v1/materials/low-stock',
    recentActivity: '/api/v1/materials/recent-activity',
    forecasts: '/api/v1/predictions/stockout',
    actionItems: '/api/v1/alerts',
  },
  reports: {
    inventoryTrends: '/api/v1/reports/inventory-trends',
    productionVolume: '/api/v1/reports/production-volume',
    consumptionTrends: '/api/v1/reports/consumption-trends',
    vendorPerformance: '/api/v1/reports/vendor-performance',
    wastageAnalysis: '/api/v1/reports/wastage-analysis',
  },
  predictions: {
    reorder: '/api/v1/predictions/reorder',
    reorderForMaterial: (materialId: string) => `/api/v1/predictions/reorder/${materialId}`,
    stockout: '/api/v1/predictions/stockout',
    suggestions: '/api/v1/predictions/suggestions',
    suggestionsGrouped: '/api/v1/predictions/suggestions/grouped',
    generateSuggestions: '/api/v1/predictions/suggestions/generate',
    generatePo: '/api/v1/predictions/generate-po',
    analytics: (materialId: string) => `/api/v1/predictions/analytics/${materialId}`,
    analyticsTrend: (materialId: string) => `/api/v1/predictions/analytics/${materialId}/trend`,
    projections: '/api/v1/predictions/projections',
    reorderConfig: (materialId: string) => `/api/v1/predictions/config/reorder/${materialId}`,
    forecast: (materialId: string) => `/api/v1/predictions/forecast/${materialId}`,
    forecastAccuracy: (materialId: string) => `/api/v1/predictions/forecast/${materialId}/accuracy`,
    correlations: (materialId: string) => `/api/v1/predictions/correlations/${materialId}`,
  },
  alerts: {
    list: '/api/v1/alerts',
    summary: '/api/v1/alerts/summary',
    get: (id: string) => `/api/v1/alerts/${id}`,
    acknowledge: (id: string) => `/api/v1/alerts/${id}/acknowledge`,
    dismiss: (id: string) => `/api/v1/alerts/${id}/dismiss`,
  },
  compliance: {
    signatures: '/api/v1/compliance/signatures',
    createSignature: '/api/v1/compliance/signatures',
    retentionPolicies: '/api/v1/compliance/retention-policies',
    createRetentionPolicy: '/api/v1/compliance/retention-policies',
    updateRetentionPolicy: (id: string) => `/api/v1/compliance/retention-policies/${id}`,
    deleteRetentionPolicy: (id: string) => `/api/v1/compliance/retention-policies/${id}`,
    dsr: '/api/v1/compliance/dsr',
    dsrRequest: '/api/v1/compliance/dsr/request',
    dsrStatus: (requestId: string) => `/api/v1/compliance/dsr/${requestId}`,
  },
  importExport: {
    items: '/api/v1/import-export/items',
    exportItems: '/api/v1/import-export/items/export',
    exportStockedValuation: '/api/v1/import-export/stocked-valuation/export',
    materials: '/api/v1/import-export/materials',
    exportMaterials: '/api/v1/import-export/materials/export',
    recipes: '/api/v1/import-export/recipes',
    importRecipes: '/api/v1/import-export/recipes/import',
    exportRecipes: '/api/v1/import-export/recipes/export',
    vendors: '/api/v1/import-export/vendors',
    exportVendors: '/api/v1/import-export/vendors/export',
  },
  webhooks: {
    list: '/api/v1/webhooks',
    create: '/api/v1/webhooks',
    get: (id: string) => `/api/v1/webhooks/${id}`,
    update: (id: string) => `/api/v1/webhooks/${id}`,
    delete: (id: string) => `/api/v1/webhooks/${id}`,
    test: (id: string) => `/api/v1/webhooks/${id}/test`,
  },
  notifications: {
    list: '/api/v1/notifications',
    markRead: (id: string) => `/api/v1/notifications/${id}/read`,
    markAllRead: '/api/v1/notifications/read-all',
    unreadCount: '/api/v1/notifications/unread-count',
    delete: (id: string) => `/api/v1/notifications/${id}`,
  },
  warehouse: {
    list: '/api/v1/warehouses',
    create: '/api/v1/warehouses',
    get: (id: string) => `/api/v1/warehouses/${id}`,
    update: (id: string) => `/api/v1/warehouses/${id}`,
    delete: (id: string) => `/api/v1/warehouses/${id}`,
    setDefault: (id: string) => `/api/v1/warehouses/${id}/set-default`,
    getLocations: (id: string) => `/api/v1/warehouses/${id}/locations`,
    createLocation: (id: string) => `/api/v1/warehouses/${id}/locations`,
    updateLocation: (id: string, locationId: string) => `/api/v1/warehouses/${id}/locations/${locationId}`,
    deleteLocation: (id: string, locationId: string) => `/api/v1/warehouses/${id}/locations/${locationId}`,
    getInventory: (id: string) => `/api/v1/warehouses/${id}/inventory`,
  },
  lots: {
    list: '/api/v1/lots',
    create: '/api/v1/lots',
    get: (id: string) => `/api/v1/lots/${id}`,
    update: (id: string) => `/api/v1/lots/${id}`,
    retire: (id: string) => `/api/v1/lots/${id}/retire`,
  },
  qc: {
    inspections: '/api/v1/qc/inspections',
    createInspection: '/api/v1/qc/inspections',
    getInspection: (id: string) => `/api/v1/qc/inspections/${id}`,
    updateInspection: (id: string) => `/api/v1/qc/inspections/${id}`,
  },
  quickbooks: {
    authUrl: '/api/v1/oauth-providers/quickbooks/auth-url',
    callback: '/api/v1/oauth-providers/callback/validate',
    disconnect: '/api/v1/quickbooks/disconnect',
    status: '/api/v1/quickbooks/status',
    syncVendors: '/api/v1/quickbooks/sync/vendors',
    syncItems: '/api/v1/quickbooks/sync/items',
    syncAll: '/api/v1/quickbooks/sync/all',
    exportPO: (id: string) => `/api/v1/quickbooks/export/po/${id}`,
    inventoryAdjustment: '/api/v1/quickbooks/sync/inventory-adjustment',
    syncStatus: '/api/v1/quickbooks/sync-status',
    refreshToken: '/api/v1/quickbooks/refresh-token',
  },
  ecommerce: {
    connections: '/api/v1/ecommerce/connections',
    getConnection: (id: string) => `/api/v1/ecommerce/connections/${id}`,
    deleteConnection: (id: string) => `/api/v1/ecommerce/connections/${id}`,
    syncProducts: (id: string) => `/api/v1/ecommerce/connections/${id}/sync/products`,
    syncInventory: (id: string) => `/api/v1/ecommerce/connections/${id}/sync/inventory`,
    syncOrders: (id: string) => `/api/v1/ecommerce/connections/${id}/sync/orders`,
    syncAll: (id: string) => `/api/v1/ecommerce/connections/${id}/sync`,
    products: (id: string) => `/api/v1/ecommerce/connections/${id}/products`,
    mapProduct: (id: string) => `/api/v1/ecommerce/connections/${id}/map`,
    autoMap: (id: string) => `/api/v1/ecommerce/connections/${id}/auto-map`,
    orders: '/api/v1/ecommerce/orders',
    processOrder: (id: string) => `/api/v1/ecommerce/orders/${id}/process`,
    shopifyAuth: (shop: string) => `/api/v1/oauth-providers/shopify/auth-url?shop=${encodeURIComponent(shop)}`,
    amazonAuth: '/api/v1/oauth-providers/amazon/auth-url',
    amazonRevoke: '/api/v1/oauth-providers/amazon/revoke',
  },
  skuMappings: {
    list: '/api/v1/sku-mappings',
    unmapped: '/api/v1/sku-mappings/unmapped',
    conflicts: '/api/v1/sku-mappings/conflicts',
    suggestions: '/api/v1/sku-mappings/suggestions',
    readiness: '/api/v1/sku-mappings/readiness',
    amazonReadiness: '/api/v1/sku-mappings/amazon-readiness',
    confirm: (id: string) => `/api/v1/sku-mappings/${id}/confirm`,
    createRecipe: (id: string) => `/api/v1/sku-mappings/${id}/create-recipe`,
    suggestBom: (id: string) => `/api/v1/sku-mappings/${id}/suggest-bom`,
    delete: (id: string, deleteInSource: boolean) =>
      `/api/v1/sku-mappings/${id}?deleteInSource=${deleteInSource ? 'true' : 'false'}`,
    bulkCleanup: '/api/v1/sku-mappings/bulk-cleanup',
    autoConfirm: '/api/v1/sku-mappings/auto-confirm',
    amazonOfferPreview: '/api/v1/sku-mappings/amazon-offers/preview',
    amazonOffers: '/api/v1/sku-mappings/amazon-offers',
    pushCanonicalSku: '/api/v1/sku-mappings/push-canonical-sku',
    pushReadyCanonicalSkus: '/api/v1/sku-mappings/push-ready-canonical-skus',
  },
  dataEnrichment: {
    preview: '/api/v1/data-enrichment/preview',
    commit: '/api/v1/data-enrichment/commit',
    batches: '/api/v1/data-enrichment/batches',
    batch: (id: string) => `/api/v1/data-enrichment/batches/${id}`,
    batchRowsCsv: (id: string) => `/api/v1/data-enrichment/batches/${id}/rows.csv`,
    revertBatch: (id: string) => `/api/v1/data-enrichment/batches/${id}/revert`,
  },
  shipping: {
    connections: '/api/v1/shipping/connections',
    createConnection: '/api/v1/shipping/connections',
    getConnection: (id: string) => `/api/v1/shipping/connections/${id}`,
    deleteConnection: (id: string) => `/api/v1/shipping/connections/${id}`,
    rates: '/api/v1/shipping/rates',
    cheapestRate: '/api/v1/shipping/rates/cheapest',
    fastestRate: '/api/v1/shipping/rates/fastest',
    createShipment: '/api/v1/shipping/shipments',
    listShipments: '/api/v1/shipping/shipments',
    shipmentStats: '/api/v1/shipping/shipments/stats',
    getShipment: (id: string) => `/api/v1/shipping/shipments/${id}`,
    createLabel: (id: string) => `/api/v1/shipping/shipments/${id}/label`,
    trackShipment: (id: string) => `/api/v1/shipping/shipments/${id}/track`,
    cancelShipment: (id: string) => `/api/v1/shipping/shipments/${id}/cancel`,
    trackByNumber: (trackingNumber: string) => `/api/v1/shipping/track/${trackingNumber}`,
    updateStatus: (id: string) => `/api/v1/shipping/shipments/${id}/status`,
  },
  ai: {
    providers: '/api/v1/ai/providers',
    configs: '/api/v1/ai/configs',
    createConfig: '/api/v1/ai/configs',
    updateConfig: (id: string) => `/api/v1/ai/configs/${id}`,
    deleteConfig: (id: string) => `/api/v1/ai/configs/${id}`,
    testConfig: (id: string) => `/api/v1/ai/configs/${id}/test`,
    setDefault: (id: string) => `/api/v1/ai/configs/${id}/default`,
    identify: '/api/v1/ai/identify',
  },
  cycleCounts: {
    list: '/api/v1/cycle-counts',
    get: (id: string) => `/api/v1/cycle-counts/${id}`,
    create: '/api/v1/cycle-counts',
    start: (id: string) => `/api/v1/cycle-counts/${id}/start`,
    submit: (id: string) => `/api/v1/cycle-counts/${id}/submit`,
    approve: (id: string) => `/api/v1/cycle-counts/${id}/approve`,
    apply: (id: string) => `/api/v1/cycle-counts/${id}/apply`,
    cancel: (id: string) => `/api/v1/cycle-counts/${id}/cancel`,
    accuracy: '/api/v1/cycle-counts/accuracy',
    abcSummary: '/api/v1/cycle-counts/abc/summary',
    abcCalculate: '/api/v1/cycle-counts/abc/calculate',
    abcDue: '/api/v1/cycle-counts/abc/due',
  },
  serials: {
    list: '/api/v1/serials',
    get: (id: string) => `/api/v1/serials/${id}`,
    create: '/api/v1/serials',
    bulkCreate: '/api/v1/serials/bulk',
    lookup: (serialNumber: string) => `/api/v1/serials/lookup/${serialNumber}`,
    history: (id: string) => `/api/v1/serials/${id}/history`,
    allocate: (id: string) => `/api/v1/serials/${id}/allocate`,
    deallocate: (id: string) => `/api/v1/serials/${id}/deallocate`,
    sell: (id: string) => `/api/v1/serials/${id}/sell`,
    scrap: (id: string) => `/api/v1/serials/${id}/scrap`,
  },
  transfers: {
    list: '/api/v1/transfers',
    get: (id: string) => `/api/v1/transfers/${id}`,
    create: '/api/v1/transfers',
    update: (id: string) => `/api/v1/transfers/${id}`,
    delete: (id: string) => `/api/v1/transfers/${id}`,
    submit: (id: string) => `/api/v1/transfers/${id}/submit`,
    ship: (id: string) => `/api/v1/transfers/${id}/ship`,
    receive: (id: string) => `/api/v1/transfers/${id}/receive`,
    cancel: (id: string) => `/api/v1/transfers/${id}/cancel`,
    pending: '/api/v1/transfers/pending',
    statistics: '/api/v1/transfers/statistics',
  },
  anomalies: {
    list: '/api/v1/anomalies',
    summary: '/api/v1/anomalies/summary',
    detect: '/api/v1/anomalies/detect',
    resolve: (id: string) => `/api/v1/anomalies/${id}/resolve`,
    exclude: (id: string) => `/api/v1/anomalies/${id}/exclude`,
    get: (id: string) => `/api/v1/anomalies/${id}`,
  },
  classification: {
    image: '/api/v1/classification/image',
    text: '/api/v1/classification/text',
    generateSku: '/api/v1/classification/generate-sku',
    duplicates: '/api/v1/classification/duplicates',
  },
  vendorIntelligence: {
    score: (vendorId: string) => `/api/v1/vendor-intelligence/${vendorId}/score`,
    leadTime: (vendorId: string) => `/api/v1/vendor-intelligence/${vendorId}/lead-time`,
    consolidation: '/api/v1/vendor-intelligence/consolidation',
    compare: (materialId: string) => `/api/v1/vendor-intelligence/compare/${materialId}`,
    rankings: '/api/v1/vendor-intelligence/rankings',
    insights: '/api/v1/vendor-intelligence/insights',
  },
  chat: {
    query: '/api/v1/chat/query',
    sessions: '/api/v1/chat/sessions',
    session: (sessionId: string) => `/api/v1/chat/sessions/${sessionId}`,
    suggestions: '/api/v1/chat/suggestions',
  },
  packing: {
    list: '/api/v1/packing',
    create: '/api/v1/packing',
    get: (id: string) => `/api/v1/packing/${id}`,
    update: (id: string) => `/api/v1/packing/${id}`,
    delete: (id: string) => `/api/v1/packing/${id}`,
    addItem: (id: string) => `/api/v1/packing/${id}/items`,
    removeItem: (id: string, itemId: string) => `/api/v1/packing/${id}/items/${itemId}`,
    pack: (id: string) => `/api/v1/packing/${id}/pack`,
    seal: (id: string) => `/api/v1/packing/${id}/seal`,
    ship: (id: string) => `/api/v1/packing/${id}/ship`,
    packingList: (id: string) => `/api/v1/packing/${id}/packing-list`,
    packStation: (id: string) => `/api/v1/packing/${id}/pack-station`,
    scan: (id: string) => `/api/v1/packing/${id}/scan`,
    stats: '/api/v1/packing/stats',
  },
  picking: {
    queue: '/api/v1/picking/queue',
    session: (orderId: string) => `/api/v1/picking/orders/${orderId}`,
    start: (orderId: string) => `/api/v1/picking/orders/${orderId}/start`,
    scan: (orderId: string) => `/api/v1/picking/orders/${orderId}/scan`,
    handoff: (orderId: string) => `/api/v1/picking/orders/${orderId}/handoff`,
  },
  rma: {
    list: '/api/v1/rma',
    create: '/api/v1/rma',
    get: (id: string) => `/api/v1/rma/${id}`,
    update: (id: string) => `/api/v1/rma/${id}`,
    approve: (id: string) => `/api/v1/rma/${id}/approve`,
    inspect: (id: string) => `/api/v1/rma/${id}/inspect`,
    resolve: (id: string) => `/api/v1/rma/${id}/resolve`,
    cancel: (id: string) => `/api/v1/rma/${id}/cancel`,
    stats: '/api/v1/rma/stats',
  },
  manufacturingOrders: {
    list: '/api/v1/manufacturing-orders',
    create: '/api/v1/manufacturing-orders',
    createRun: '/api/v1/manufacturing-orders/runs',
    getRun: (id: string) => `/api/v1/manufacturing-orders/runs/${id}`,
    get: (id: string) => `/api/v1/manufacturing-orders/${id}`,
    update: (id: string) => `/api/v1/manufacturing-orders/${id}`,
    schedule: (id: string) => `/api/v1/manufacturing-orders/${id}/schedule`,
    start: (id: string) => `/api/v1/manufacturing-orders/${id}/start`,
    complete: (id: string) => `/api/v1/manufacturing-orders/${id}/complete`,
    close: (id: string) => `/api/v1/manufacturing-orders/${id}/close`,
    cancel: (id: string) => `/api/v1/manufacturing-orders/${id}/cancel`,
    stats: '/api/v1/manufacturing-orders/stats',
  },
  labor: {
    clockIn: '/api/v1/labor/clock-in',
    clockOut: (id: string) => `/api/v1/labor/${id}/clock-out`,
    addBreak: (id: string) => `/api/v1/labor/${id}/break`,
    manual: '/api/v1/labor/manual',
    mine: '/api/v1/labor/mine',
    summary: '/api/v1/labor/summary',
    byMO: (moId: string) => `/api/v1/labor/manufacturing-order/${moId}`,
    byBatch: (batchId: string) => `/api/v1/labor/production-batch/${batchId}`,
    delete: (id: string) => `/api/v1/labor/${id}`,
  },
  approvals: {
    workflows: '/api/v1/approvals/workflows',
    createWorkflow: '/api/v1/approvals/workflows',
    getWorkflow: (id: string) => `/api/v1/approvals/workflows/${id}`,
    updateWorkflow: (id: string) => `/api/v1/approvals/workflows/${id}`,
    deleteWorkflow: (id: string) => `/api/v1/approvals/workflows/${id}`,
    submit: '/api/v1/approvals/submit',
    pending: '/api/v1/approvals/pending',
    getRecord: (id: string) => `/api/v1/approvals/records/${id}`,
    decide: (id: string) => `/api/v1/approvals/records/${id}/decide`,
    cancelRecord: (id: string) => `/api/v1/approvals/records/${id}/cancel`,
    entityRecords: (entityType: string, entityId: string) =>
      `/api/v1/approvals/entity/${entityType}/${entityId}`,
  },
  subcontracting: {
    list: '/api/v1/subcontracting',
    create: '/api/v1/subcontracting',
    get: (id: string) => `/api/v1/subcontracting/${id}`,
    update: (id: string) => `/api/v1/subcontracting/${id}`,
    delete: (id: string) => `/api/v1/subcontracting/${id}`,
    send: (id: string) => `/api/v1/subcontracting/${id}/send`,
    acknowledge: (id: string) => `/api/v1/subcontracting/${id}/acknowledge`,
    start: (id: string) => `/api/v1/subcontracting/${id}/start`,
    qualityCheck: (id: string) => `/api/v1/subcontracting/${id}/quality-check`,
    complete: (id: string) => `/api/v1/subcontracting/${id}/complete`,
    cancel: (id: string) => `/api/v1/subcontracting/${id}/cancel`,
    addItem: (id: string) => `/api/v1/subcontracting/${id}/items`,
    removeItem: (id: string, itemId: string) => `/api/v1/subcontracting/${id}/items/${itemId}`,
    stats: '/api/v1/subcontracting/stats',
  },
  customFields: {
    definitions: '/api/v1/custom-fields/definitions',
    getDefinition: (id: string) => `/api/v1/custom-fields/definitions/${id}`,
    updateDefinition: (id: string) => `/api/v1/custom-fields/definitions/${id}`,
    deleteDefinition: (id: string) => `/api/v1/custom-fields/definitions/${id}`,
    getValues: (entityType: string, entityId: string) =>
      `/api/v1/custom-fields/entity/${entityType}/${entityId}`,
    setValues: (entityType: string, entityId: string) =>
      `/api/v1/custom-fields/entity/${entityType}/${entityId}`,
    deleteValue: (entityType: string, entityId: string, fieldKey: string) =>
      `/api/v1/custom-fields/entity/${entityType}/${entityId}/${fieldKey}`,
  },
  capacity: {
    resources: '/api/v1/capacity/resources',
    createResource: '/api/v1/capacity/resources',
    getResource: (id: string) => `/api/v1/capacity/resources/${id}`,
    updateResource: (id: string) => `/api/v1/capacity/resources/${id}`,
    deleteResource: (id: string) => `/api/v1/capacity/resources/${id}`,
    scheduleResource: (id: string) => `/api/v1/capacity/resources/${id}/schedule`,
    getSchedule: (id: string) => `/api/v1/capacity/resources/${id}/schedule`,
    removeSchedule: (id: string) => `/api/v1/capacity/schedules/${id}`,
    overview: '/api/v1/capacity/overview',
    bottlenecks: '/api/v1/capacity/bottlenecks',
    availability: (id: string) => `/api/v1/capacity/resources/${id}/availability`,
  },
  mps: {
    plans: '/api/v1/mps/plans',
    createPlan: '/api/v1/mps/plans',
    getPlan: (id: string) => `/api/v1/mps/plans/${id}`,
    updatePlan: (id: string) => `/api/v1/mps/plans/${id}`,
    deletePlan: (id: string) => `/api/v1/mps/plans/${id}`,
    publish: (id: string) => `/api/v1/mps/plans/${id}/publish`,
    activate: (id: string) => `/api/v1/mps/plans/${id}/activate`,
    complete: (id: string) => `/api/v1/mps/plans/${id}/complete`,
    archive: (id: string) => `/api/v1/mps/plans/${id}/archive`,
    items: (planId: string) => `/api/v1/mps/plans/${planId}/items`,
    updateItem: (itemId: string) => `/api/v1/mps/items/${itemId}`,
    deleteItem: (itemId: string) => `/api/v1/mps/items/${itemId}`,
    materialRequirements: (planId: string) => `/api/v1/mps/plans/${planId}/material-requirements`,
    generateMOs: (planId: string) => `/api/v1/mps/plans/${planId}/generate-mos`,
    demand: '/api/v1/mps/demand',
    setDemand: '/api/v1/mps/demand',
    generateDemand: '/api/v1/mps/demand/generate',
  },
} as const;

// ============================================
// Storage Keys
// ============================================

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'stockforge_auth_token',
  REFRESH_TOKEN: 'stockforge_refresh_token',
  USER: 'stockforge_user',
  THEME_MODE: 'stockforge_theme_mode',
  SIDEBAR_COLLAPSED: 'stockforge_sidebar_collapsed',
  OFFLINE_QUEUE: 'stockforge_offline_queue',
  RECENT_SEARCHES: 'stockforge_recent_searches',
} as const;

// ============================================
// Color Palette - Industrial Precision Design System
// ============================================

export const COLORS = {
  // Forge Teal - Primary Action Color
  primary: {
    50: '#F0FDFA',
    100: '#CCFBF1',
    200: '#99F6E4',
    300: '#5EEAD4',
    400: '#2DD4BF',
    500: '#14B8A6', // Primary
    600: '#0D9488',
    700: '#0F766E',
    800: '#115E59',
    900: '#134E4A',
  },
  // Blueprint Blue - Secondary Action Color
  secondary: {
    50: '#E3F2FD',
    100: '#BBDEFB',
    200: '#90CAF9',
    300: '#64B5F6',
    400: '#42A5F5',
    500: '#2B5797', // Primary
    600: '#1E4A7D',
    700: '#153D63',
    800: '#0D2F4A',
    900: '#071D2F',
  },
  // Success Green - High Contrast Industrial
  success: {
    50: '#E8F5E9',
    100: '#C8E6C9',
    200: '#A5D6A7',
    300: '#81C784',
    400: '#66BB6A',
    500: '#4CAF50',
    600: '#43A047',
    700: '#388E3C',
    800: '#2E7D32', // Primary for status
    900: '#1B5E20',
  },
  // Warning Amber - High Visibility
  warning: {
    50: '#FFF8E1',
    100: '#FFECB3',
    200: '#FFE082',
    300: '#FFD54F',
    400: '#FFCA28',
    500: '#FFC107',
    600: '#FFB300',
    700: '#FFA000',
    800: '#F9A825', // Primary for status
    900: '#F57F17',
  },
  // Critical Red - High Saturation
  error: {
    50: '#FFEBEE',
    100: '#FFCDD2',
    200: '#EF9A9A',
    300: '#E57373',
    400: '#EF5350',
    500: '#F44336',
    600: '#E53935',
    700: '#D32F2F',
    800: '#C62828', // Primary for status
    900: '#B71C1C',
  },
  // Neutral - Warm Workshop Grays
  neutral: {
    0: '#FAFAF8', // Paper White
    50: '#F5F5F3',
    100: '#E8E6E3', // Concrete
    200: '#D5D3D0',
    300: '#B8B6B3',
    400: '#8A8886',
    500: '#6B6967',
    600: '#4A4847',
    700: '#3A3A3C', // Workshop Gray
    800: '#2D2D2D', // Slate Workshop
    900: '#1A1A1E',
    1000: '#000000', // OLED Black
  },
  // Inventory Status - High Contrast
  inventory: {
    healthy: '#2E7D32',
    healthyLight: '#4CAF50',
    warning: '#F9A825',
    warningLight: '#FFC107',
    critical: '#C62828',
    criticalLight: '#EF5350',
    outOfStock: '#424242',
    outOfStockLight: '#757575',
  },
  // PO Status Colors
  poStatus: {
    draft: '#6B6967',
    pendingApproval: '#F9A825',
    sent: '#2B5797',
    confirmed: '#00897B',
    received: '#2E7D32',
    cancelled: '#C62828',
  },
  // Material Category Colors
  materialCategory: {
    liquid: '#2B5797',
    fabric: '#7B1FA2',
    discrete: '#0F766E',
  },
  // Batch Override - Muted Amber Mode
  batchOverride: {
    background: '#D97706',
    backgroundLight: '#FFFBEB',
    text: '#92400E',
    border: '#D97706',
  },
  // Special Batch Highlight
  specialBatch: {
    background: '#D97706',
    backgroundLight: '#FFFBEB',
    text: '#92400E',
    border: '#D97706',
  },
  // Semantic UI Colors
  semantic: {
    forgeTeal: '#0F766E',
    mutedAmber: '#D97706',
    blueprintBlue: '#2B5797',
    burntOrange: '#D97706',
    slateWorkshop: '#2D2D2D',
    workshopGray: '#3A3A3C',
    concrete: '#E8E6E3',
    paperWhite: '#FAFAF8',
  },
} as const;

// ============================================
// Spacing Tokens
// ============================================

export const SPACING = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  '2xl': 24,
  '3xl': 32,
  '4xl': 40,
  '5xl': 48,
  '6xl': 64,
} as const;

// ============================================
// Border Radius Tokens - Industrial Sharp Edges
// ============================================

export const BORDER_RADIUS = {
  none: 0,
  xs: 1,
  sm: 2, // Primary for most elements
  md: 2, // Kept minimal for industrial look
  lg: 4,
  xl: 4,
  '2xl': 4,
  full: 9999, // For avatars only
} as const;

// ============================================
// Typography - Industrial Precision
// ============================================

export const TYPOGRAPHY = {
  fontFamily: {
    primary: "'IBM Plex Sans', 'JetBrains Mono', system-ui, sans-serif",
    mono: "'JetBrains Mono', 'IBM Plex Mono', 'Fira Code', monospace",
    header: "'IBM Plex Sans', system-ui, sans-serif",
  },
  fontSize: {
    xs: 11,
    sm: 12,
    base: 14,
    lg: 16,
    xl: 18,
    '2xl': 20,
    '3xl': 24,
    '4xl': 30,
    '5xl': 36,
  },
  fontWeight: {
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
  },
  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em', // For uppercase labels
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// ============================================
// Haptic Feedback Patterns
// ============================================

export const HAPTIC_PATTERNS = {
  // Successful barcode/QR scan
  successfulScan: {
    type: 'impact',
    style: 'medium',
    duration: 100,
  },
  // Item successfully added
  itemAdded: {
    type: 'double-impact',
    style: 'light',
    duration: [50, 50],
    gap: 50,
  },
  // Critical stock threshold
  criticalWarning: {
    type: 'triple-impact',
    style: 'heavy',
    duration: [100, 100, 100],
    gap: 50,
  },
  // Error/failed operation
  error: {
    type: 'notification',
    style: 'error',
    duration: 300,
  },
  // Navigation tap
  navigationTap: {
    type: 'impact',
    style: 'light',
    duration: 10,
  },
  // Batch override activated
  batchOverride: {
    type: 'pattern',
    style: 'mixed',
    duration: [50, 100, 50],
    gap: 100,
  },
  // Selection changed
  selectionChanged: {
    type: 'selection',
    duration: 10,
  },
} as const;

// ============================================
// Z-Index Scale
// ============================================

export const Z_INDEX = {
  base: 0,
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modal: 1300,
  popover: 1400,
  tooltip: 1500,
  toast: 1600,
} as const;

// ============================================
// Animation Durations
// ============================================

export const ANIMATION_DURATION = {
  instant: 0,
  fast: 150,
  normal: 250,
  slow: 400,
  slower: 600,
} as const;

// ============================================
// Validation Constants
// ============================================

export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 12,
  PASSWORD_MAX_LENGTH: 128,
  EMAIL_MAX_LENGTH: 255,
  NAME_MAX_LENGTH: 100,
  SKU_MIN_LENGTH: 3,
  SKU_MAX_LENGTH: 50,
  DESCRIPTION_MAX_LENGTH: 1000,
  NOTES_MAX_LENGTH: 2000,
} as const;

// ============================================
// Production Constants
// ============================================

export const PRODUCTION = {
  DEFAULT_WASTAGE_FACTOR: 0.02,
  MAX_BATCH_SIZE: 10000,
  MIN_BATCH_SIZE: 1,
  CONFIDENCE_HIGH_THRESHOLD: 0.85,
  CONFIDENCE_MEDIUM_THRESHOLD: 0.60,
} as const;

// ============================================
// Navigation Items
// ============================================

export const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', path: '/', icon: 'LayoutDashboard' },
  { id: 'produce', label: 'Build / Produce', path: '/produce', icon: 'Factory' },
  { id: 'inventory', label: 'Inventory', path: '/inventory', icon: 'Package' },
  { id: 'recipes', label: 'Recipes (BOM)', path: '/recipes', icon: 'FileText' },
  { id: 'scan', label: 'Scan / Vision', path: '/scan', icon: 'ScanLine' },
  { id: 'supply-chain', label: 'Supply Chain', path: '/supply-chain', icon: 'Truck' },
  { id: 'reports', label: 'Reports', path: '/reports', icon: 'BarChart3' },
  { id: 'settings', label: 'Settings', path: '/settings', icon: 'Settings' },
] as const;
